import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Joyride, STATUS } from 'react-joyride';
import { PiXLight, PiCaretRightLight, PiArrowRightLight } from 'react-icons/pi';
import { authAPI } from '../../services/api';
import { useAuthStore } from '../../store';
import profileSetupImg from '../../assets/images/config.png';
import jobCreationImg from '../../assets/images/create_job.png';
import logoMark from '../../assets/images/sanspath-logo-mark.png';

// Enterprise Pattern: Extract storage keys to avoid magic strings and collisions
const TOUR_STORAGE_KEY = 'sanspath_employer_onboarding_v1_completed';
const TOUR_STORAGE_KEY_PREFIX = 'sanspath_employer_onboarding_v1_completed_user';

const CustomTooltip = ({
  index,
  step,
  tooltipProps,
  primaryProps,
  skipProps,
  isLastStep,
}) => {
  return (
    <div 
      {...tooltipProps} 
      // Enterprise Pattern: Proper ARIA semantics for accessibility
      role="dialog"
      aria-modal="true"
      aria-labelledby={`tour-step-${index}-title`}
      className="relative w-[90vw] max-w-[1080px] overflow-hidden rounded-2xl bg-white shadow-[0_16px_40px_rgba(11,31,58,0.12)] ring-1 ring-black/5 flex flex-col md:flex-row md:min-h-[480px]"
    >
      {/* Left Side: Visual Guide */}
      <div className="relative flex w-full flex-col items-center justify-center overflow-hidden bg-gradient-to-br from-sky-50 to-blue-100 p-6 md:p-10 md:w-[50%]">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-white/40 via-transparent to-transparent" />
        
        <div className="relative z-10 w-full flex justify-center">
          <div className="relative w-full max-w-[480px]">
            <div className="absolute -inset-1 rounded-2xl bg-gradient-to-tr from-sky-400/20 to-blue-500/20 blur-xl" />
            <div className="relative overflow-hidden rounded-xl border border-slate-200/50 bg-white shadow-[0_20px_40px_rgba(11,31,58,0.08)]">
              {/* Browser Header */}
              <div className="flex items-center justify-center border-b border-slate-100 bg-[#f8fafc]/90 px-4 py-2 backdrop-blur-sm">
                <div className="absolute left-3.5 flex items-center gap-1.5">
                  <div className="h-2.5 w-2.5 rounded-full bg-[#ff5f56]" />
                  <div className="h-2.5 w-2.5 rounded-full bg-[#ffbd2e]" />
                  <div className="h-2.5 w-2.5 rounded-full bg-[#27c93f]" />
                </div>
                <div className="flex h-5 w-1/2 items-center justify-center rounded bg-slate-200/50 px-2">
                  <span className="text-[10px] font-medium tracking-wide text-slate-500">employer.sanspath.com</span>
                </div>
              </div>
              <div className="bg-white">
                <img 
                  src={step.image} 
                  alt={step.title} 
                  className="h-auto w-full object-cover transition-opacity duration-300" 
                  loading="eager" 
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side: Content & Actions */}
      <div className="flex w-full flex-col bg-white p-6 md:p-10 md:w-[50%]">
        <header className="mb-8 flex items-center justify-between md:mb-10">
          <div className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center">
              <img src={logoMark} alt="SansPath" className="h-6 w-auto object-contain" />
            </div>
            <h2 className="font-display text-[15px] font-medium uppercase tracking-widest text-slate-900">
              Welcome to SansPath
            </h2>
          </div>
          <button
            {...skipProps}
            aria-label="Dismiss guide"
            className="group flex h-8 w-8 items-center justify-center rounded-full bg-slate-50 text-slate-400 transition-all hover:bg-slate-100 hover:text-slate-700"
          >
            <PiXLight size={20} className="transition-transform group-hover:rotate-90" aria-hidden="true" />
          </button>
        </header>
        
        <main className="flex-1">
          <h3 id={`tour-step-${index}-title`} className="mb-2 font-display text-2xl font-normal tracking-tight text-slate-900 md:text-3xl">
            {step.title}
          </h3>
          <p className="mb-6 text-[15px] text-slate-500 md:mb-8 md:text-[17px]">
            {step.subtitle}
          </p>
          {step.content}
        </main>

        <footer className="mt-8 flex items-center justify-between border-t border-slate-100 pt-6">
          <button
            {...skipProps}
            className="text-[14px] font-medium text-slate-400 transition-colors hover:text-slate-600"
          >
            Skip for now
          </button>
          
          <div className="flex gap-3">
             {step.extraAction && (
               <button
                 onClick={step.extraAction}
                 className="hidden rounded-xl border border-slate-200 bg-white px-6 py-3 text-[14px] font-medium text-slate-700 transition-all hover:border-slate-300 hover:bg-slate-50 md:block"
               >
                 {step.actionText}
               </button>
             )}
            <button
              {...primaryProps}
              className="group flex items-center gap-2 rounded-xl bg-slate-900 px-5 py-3 text-[14px] font-medium text-white transition-all hover:bg-slate-800 md:px-6"
            >
              {isLastStep ? 'Finish' : 'Continue'}
              <PiCaretRightLight size={18} className="transition-transform group-hover:translate-x-1" aria-hidden="true" />
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
};

const DashboardGuideModal = () => {
  const navigate = useNavigate();
  const user = useAuthStore(state => state.user);
  const updateUser = useAuthStore(state => state.updateUser);
  const [run, setRun] = useState(false);
  const seenMarkedRef = useRef(false);
  const userStorageKey = user?.id ? `${TOUR_STORAGE_KEY_PREFIX}_${user.id}` : TOUR_STORAGE_KEY;

  // Enterprise Pattern: Abstracted storage handling wrapped in try-catch to prevent crash if localStorage is disabled
  const markTourAsSeen = useCallback(async ({ close = true } = {}) => {
    const fallbackSeenAt = user?.dashboardGuideSeenAt || new Date().toISOString();

    try {
      localStorage.setItem(TOUR_STORAGE_KEY, 'true');
      localStorage.setItem(userStorageKey, 'true');
    } catch (e) {
      console.warn('SansPath Onboarding: Could not save tour state to local storage', e);
    }

    if (close) setRun(false);

    if (seenMarkedRef.current) return;
    seenMarkedRef.current = true;
    updateUser({
      dashboardGuideSeenAt: fallbackSeenAt,
      shouldShowDashboardGuide: false
    });

    try {
      const response = await authAPI.markDashboardGuideSeen();
      const seenAt = response?.dashboardGuide?.seenAt || fallbackSeenAt;
      updateUser({
        dashboardGuideSeenAt: seenAt,
        dashboardGuideEligibleAt: response?.dashboardGuide?.eligibleAt || user?.dashboardGuideEligibleAt,
        shouldShowDashboardGuide: false
      });
    } catch (e) {
      console.warn('SansPath Onboarding: Could not persist dashboard guide state', e);
    }
  }, [updateUser, user?.dashboardGuideEligibleAt, user?.dashboardGuideSeenAt, userStorageKey]);

  useEffect(() => {
    // Enterprise Pattern: Preload heavy assets to ensure the modal doesn't flicker blank images when opened
    const preloadImages = [profileSetupImg, jobCreationImg];
    preloadImages.forEach(src => {
      const img = new Image();
      img.src = src;
    });

    if (!user?.id) return undefined;
    if (user.dashboardGuideSeenAt) {
      try {
        localStorage.setItem(userStorageKey, 'true');
      } catch (e) {
        console.warn('SansPath Onboarding: Could not mirror guide state to local storage', e);
      }
      return undefined;
    }

    let active = true;
    let timer = null;

    const hasLocalUserSeen = () => {
      try {
        return localStorage.getItem(userStorageKey) === 'true';
      } catch (e) {
        console.warn('SansPath Onboarding: Local storage is not accessible', e);
        return false;
      }
    };

    const openOnce = () => {
      timer = setTimeout(() => {
        markTourAsSeen({ close: false });
        if (active) setRun(true);
      }, 800);
    };

    const resolveGuideState = async () => {
      try {
        const response = await authAPI.getDashboardGuideState();
        if (!active) return;

        const guide = response?.dashboardGuide || {};
        if (guide.seenAt) {
          updateUser({
            dashboardGuideSeenAt: guide.seenAt,
            dashboardGuideEligibleAt: guide.eligibleAt || user.dashboardGuideEligibleAt,
            shouldShowDashboardGuide: false
          });
          try {
            localStorage.setItem(userStorageKey, 'true');
          } catch (e) {
            console.warn('SansPath Onboarding: Could not mirror guide state to local storage', e);
          }
          return;
        }

        if (!guide.shouldShow) {
          updateUser({
            dashboardGuideEligibleAt: guide.eligibleAt || user.dashboardGuideEligibleAt,
            shouldShowDashboardGuide: false
          });
          return;
        }

        if (hasLocalUserSeen()) {
          await markTourAsSeen({ close: false });
          return;
        }

        openOnce();
      } catch (e) {
        console.warn('SansPath Onboarding: Could not load dashboard guide state', e);
        if (user.shouldShowDashboardGuide === true && !hasLocalUserSeen()) {
          openOnce();
        }
      }
    };

    resolveGuideState();

    return () => {
      active = false;
      if (timer) clearTimeout(timer);
    };
  }, [markTourAsSeen, updateUser, user?.dashboardGuideEligibleAt, user?.dashboardGuideSeenAt, user?.id, user?.shouldShowDashboardGuide, userStorageKey]);

  useEffect(() => {
    try {
      if (localStorage.getItem(userStorageKey) === 'true') setRun(false);
    } catch (e) {
      console.warn('SansPath Onboarding: Local storage is not accessible', e);
    }
  }, [userStorageKey]);

  // Enterprise Pattern: Memoized callback prevents unnecessary react-joyride re-renders
  const handleJoyrideCallback = useCallback((data) => {
    const { status } = data;
    const finishedStatuses = [STATUS.FINISHED, STATUS.SKIPPED];

    if (finishedStatuses.includes(status)) {
      markTourAsSeen();
    }
  }, [markTourAsSeen]);

  // Enterprise Pattern: Memoized configuration objects
  const steps = useMemo(() => [
    {
      target: 'body',
      placement: 'center',
      title: 'Complete Your Profile',
      subtitle: 'Give our Engine the context it needs.',
      image: profileSetupImg,
      actionText: 'Setup Profile',
      extraAction: () => {
        markTourAsSeen();
        navigate('/profile?tab=culture&focus=company-questions');
      },
      content: (
        <div className="space-y-4 md:space-y-6">
          <p className="text-[14px] leading-relaxed text-slate-600 md:text-[15px]">
            A precise profile allows our AI matching engine to deeply understand your culture, operating model, and support style—connecting you with talent that truly fits.
          </p>
          <div className="relative overflow-hidden rounded-xl border border-sky-100 bg-[#f8fafc] p-3 md:p-4">
            <h4 className="mb-1 text-[12px] font-semibold tracking-wide text-slate-800 md:text-[13px]">The Impact</h4>
            <p className="text-[12px] leading-relaxed text-slate-600 md:text-[13px]">
              Employers with complete profiles see a <strong>40% increase in match accuracy</strong> and significantly higher conversion rates.
            </p>
          </div>
          <div className="space-y-2 pt-1">
            <h4 className="text-[11px] font-semibold uppercase tracking-widest text-slate-400">Required Context</h4>
            <ul className="space-y-2 text-[13px] text-slate-700 md:text-[14px]">
              <li className="flex items-start gap-3">
                <div className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-slate-900" /> 
                <span className="leading-snug">Company details & core industry</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-slate-900" /> 
                <span className="leading-snug">Culture & operational questionnaire</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-slate-900" /> 
                <span className="leading-snug">Benefits, perks, and working model</span>
              </li>
            </ul>
          </div>
        </div>
      ),
      disableBeacon: true,
    },
    {
      target: 'body',
      placement: 'center',
      title: 'Create Your First Role',
      subtitle: 'Engage with elite, pre-vetted talent.',
      image: jobCreationImg,
      actionText: 'Create Job',
      extraAction: () => {
        markTourAsSeen();
        navigate('/jobs/create');
      },
      content: (
        <div className="space-y-4 md:space-y-6">
          <p className="text-[14px] leading-relaxed text-slate-600 md:text-[15px]">
            Define your open position with clear requirements. Once published, the SansPath engine parses the job taxonomy and instantly evaluates the entire talent pool against it.
          </p>
          <div className="relative overflow-hidden rounded-xl border border-sky-100 bg-[#f8fafc] p-3 md:p-4">
            <h4 className="mb-1 text-[12px] font-semibold tracking-wide text-slate-800 md:text-[13px]">The Workflow</h4>
            <div className="mt-2 flex flex-wrap items-center gap-2 text-[12px] font-medium text-slate-700 md:text-[13px]">
              <span className="rounded-md border border-slate-200 bg-white px-2 py-1">Define</span>
              <PiArrowRightLight size={14} className="text-slate-400" />
              <span className="rounded-md border border-slate-200 bg-white px-2 py-1">Publish</span>
              <PiArrowRightLight size={14} className="text-slate-400" />
              <span className="rounded-md border border-slate-900 bg-slate-900 px-2 py-1 text-white">Match</span>
            </div>
          </div>
          <div className="space-y-2 pt-1">
            <h4 className="text-[11px] font-semibold uppercase tracking-widest text-slate-400">Pro Tips for Success</h4>
            <ul className="space-y-2 text-[13px] text-slate-700 md:text-[14px]">
              <li className="flex items-start gap-3">
                <div className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-slate-900" /> 
                <span className="leading-snug">Distinguish clearly between "must-have" and "nice-to-have" skills.</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-slate-900" /> 
                <span className="leading-snug">Include screening questions to automatically evaluate candidate fit.</span>
              </li>
            </ul>
          </div>
        </div>
      ),
    }
  ], [markTourAsSeen, navigate]);

  return (
    <Joyride
      steps={steps}
      run={run}
      continuous
      scrollToFirstStep
      showProgress={false}
      showSkipButton
      callback={handleJoyrideCallback}
      tooltipComponent={CustomTooltip}
      styles={{
        options: {
          zIndex: 10000,
          overlayColor: 'rgba(11, 31, 58, 0.4)',
        },
      }}
    />
  );
};

export default DashboardGuideModal;
